import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../App.css";

const DEFAULT_BACK_TO = { to: "/", label: "Back to Home" };

// Shared reading-document shell for long-form policy pages (Rules today;
// future Terms/Privacy can reuse it). Owns presentation only — layout,
// typography, table of contents, anchor scroll, back-nav, print — never
// moderation/enforcement/account-standing logic, which stays in the pages
// that render this.
export default function PolicyDocumentLayout({ title, subtitle, lastUpdated, backTo = DEFAULT_BACK_TO, toc = [], contentReady = true, children }) {
  const location = useLocation();

  // React Router's client-side navigation (Link, from a different route)
  // doesn't scroll to a #hash target the way a native page load does — only
  // same-page plain <a href="#id"> clicks get that for free. This closes
  // that gap; scroll-margin-top (App.css) handles clearing the sticky header
  // once the browser actually scrolls here.
  //
  // `contentReady` (default true, so any caller not yet passing it keeps
  // today's exact behavior) guards against firing before the target heading
  // actually exists — a caller whose `children` load asynchronously (e.g.
  // PolicyPage fetching Markdown) should flip this to true only once the
  // real content has rendered; a direct load of /rules#<anchor> would
  // otherwise have a `location.hash` that's already set on the very first
  // render (so this effect never re-runs later) and try to scroll to a
  // heading that doesn't exist in the DOM yet.
  useEffect(() => {
    if (!location.hash || !contentReady) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView();
  }, [location.hash, contentReady]);

  return (
    <section className="policy-shell">
      <div className="policy-document">
        <Link to={backTo.to} className="policy-back-link">
          <span aria-hidden="true">←</span> {backTo.label}
        </Link>

        <p className="policy-eyebrow">Who Would Win</p>
        <h1 className="policy-title">{title}</h1>
        {subtitle && <p className="policy-subtitle">{subtitle}</p>}
        {lastUpdated && <p className="policy-updated">Last updated: {lastUpdated}</p>}

        {toc.length > 0 && (
          <nav className="policy-toc" aria-label="Table of contents">
            <p className="policy-toc-heading">On this page</p>
            <ol className="policy-toc-list">
              {toc.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`}>{item.label}</a>
                  {item.children?.length > 0 && (
                    <ol className="policy-toc-sublist">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <a href={`#${child.id}`}>{child.label}</a>
                        </li>
                      ))}
                    </ol>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="policy-body">{children}</div>
      </div>
    </section>
  );
}
